import { useMediaQuery } from "@base-ui/react/unstable-use-media-query";
import type { ReactNode } from "react";
import riksuMark from "../assets/riksu-logo.png";
import riksuSignInArt from "../assets/riksu-sign-in-artwork.webp";
import { useT } from "../shell/i18n";

/** The branded, credential-free frame around Riksu's relying-party ceremony. */
export function SignInFrame({ children }: { readonly children: ReactNode }) {
  const t = useT();
  const desktop = useMediaQuery("(min-width: 64rem)", { defaultMatches: false, noSsr: true });
  return (
    // The primary half grows; the story has one fixed reading width. That reproduces the
    // useful proportion in the reference without making a 1024px laptop squeeze the way
    // in. Below `lg` the supplemental half is absent and the action remains first in the
    // DOM, the tab order and the visual order.
    <div data-shell="" className="relative isolate flex min-h-dvh bg-surface-shell text-on-shell">
      <main className="relative isolate grid min-h-dvh min-w-0 grow grid-rows-[1fr_auto] overflow-hidden bg-surface-shell px-6 py-8 lg:min-h-full lg:px-12 lg:py-10">
        <div className="flex items-center justify-center">
          <div className="flex w-full max-w-sm flex-col items-center gap-8 text-center">
            <div className="flex flex-col items-center gap-5">
              <img
                src={riksuMark}
                alt=""
                aria-hidden="true"
                className="size-10 object-contain brightness-0 invert"
              />
              <div className="flex flex-col items-center gap-2">
                <h1 className="m-0 text-[length:var(--type-auth-title-size)] leading-[var(--type-auth-title-leading)] font-medium tracking-[var(--type-auth-title-tracking)]">
                  {t("signIn.welcome")}
                </h1>
                <p className="m-0 max-w-sm text-shell-body text-on-shell-muted">
                  {t("signIn.prompt")}
                </p>
              </div>
            </div>
            <div className="w-full">{children}</div>
          </div>
        </div>

        <footer className="text-center text-shell-caption text-on-shell-muted">
          {t("signIn.company")}
        </footer>
      </main>

      <aside
        aria-labelledby="sign-in-story-heading"
        className="relative isolate hidden min-h-dvh w-80 shrink-0 overflow-hidden border-l border-surface-shell-selected bg-surface-shell lg:flex xl:w-96"
      >
        <div className="absolute inset-x-0 top-0 h-3/5 overflow-hidden">
          {desktop ? (
            <img
              src={riksuSignInArt}
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="absolute inset-0 size-full object-cover object-top opacity-80"
            />
          ) : null}
          <div aria-hidden="true" className="sign-in-visual-shade absolute inset-0" />
        </div>

        <div className="relative z-(--layer-raised) flex w-full flex-col justify-end gap-5 p-8 xl:p-10">
          <p className="m-0 text-shell-caption font-medium text-indicator-interactive">
            {t("signIn.highlights")}
          </p>
          <div className="flex flex-col gap-3">
            <h2
              id="sign-in-story-heading"
              className="m-0 text-[length:var(--type-shell-title-size)] leading-[var(--type-shell-title-leading)] font-medium"
            >
              {t("signIn.story.heading")}
            </h2>
            <p className="m-0 text-shell-body text-on-shell-muted">{t("signIn.story.body")}</p>
          </div>
        </div>
      </aside>
    </div>
  );
}
